<template>
  <div class="sl-config-container">
    <SectionLoader :status="loading" :opacity="1" />
    <template v-if="!loading">
      <div class="sl-config">
        <StoreLocatorConfigBasic
          :config="config"
          @form-change="basicData = $event"
          @file-change="logoFile = $event"
          @favicon-change="faviconFile = $event"
        />
        <v-divider class="my-6"></v-divider>
        <div class="sl-config__row">
          <div class="sl-config__col">
            <StoreLocatorConfigFacilities
              v-model="facilitiesGroups"
              :facilities-count="facilitiesCount"
            />
            <StoreLocatorConfigPosts />
            <StoreLocatorConfigPhotos />
            <StoreLocatorConfigFloatingButton
              :config="config.extra_data"
              @form-change="floatingButtonData = $event"
            />
          </div>
          <div class="sl-config__col">
            <StoreLocatorConfigReviews
              v-model="visibleRatings"
              :only-with-text.sync="showOnlyWithText"
            />
            <StoreLocatorConfigSearchRegions
              v-model="selecteDcountries"
              :choices="countries"
            />
          </div>
        </div>
        <div class="sl-config__actions">
          <ui-button color="primary" @click="saveConfig">{{
            $t("message.storeLocator.saveChanges")
          }}</ui-button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import StoreLocatorConfigBasic from "@/components/StoreLocator/StoreLocatorConfigBasic";
import StoreLocatorConfigPhotos from "@/components/StoreLocator/StoreLocatorConfigPhotos";
import StoreLocatorConfigReviews from "@/components/StoreLocator/StoreLocatorConfigReviews";
import StoreLocatorConfigPosts from "@/components/StoreLocator/StoreLocatorConfigPosts";
import StoreLocatorConfigFacilities from "@/components/StoreLocator/StoreLocatorConfigFacilities";
import StoreLocatorConfigFloatingButton from "@/components/StoreLocator/StoreLocatorConfigFloatingButton.vue";
import StoreLocatorConfigSearchRegions from "@/components/StoreLocator/StoreLocatorConfigSearchRegions";
import serviceHandler from "@/helpers/handlers/service";
import storeLocator from "@/services/storeLocator";
import { mapState } from "vuex";
import SectionLoader from "@/components/Loader/SectionLoader";
import facilitiesService from "@/services/facilities";

export default {
  name: "StoreLocatorConfig",
  components: {
    StoreLocatorConfigBasic,
    StoreLocatorConfigPhotos,
    StoreLocatorConfigReviews,
    StoreLocatorConfigPosts,
    StoreLocatorConfigFacilities,
    StoreLocatorConfigFloatingButton,
    StoreLocatorConfigSearchRegions,
    SectionLoader
  },
  props: {
    config: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: true,
      basicData: null,
      floatingButtonData: null,
      logoFile: null,
      faviconFile: null,
      facilitiesGroups: null,
      visibleRatings: null,
      selecteDcountries: [],
      noConfig: false,
      showOnlyWithText: false,
      countries: []
    };
  },

  computed: {
    ...mapState({
      facilitiesCount: state => state.company.company.facilities_count
    }),
    isStoreLocatorEnabled() {
      return this.$store.state.company.company.enabled_store_locator;
    }
  },

  async created() {
    this.loading = true;
    await this.fetchCountries();
    this.setDefaultValuesIfConfigExists();
    this.loading = false;
  },

  methods: {
    setDefaultValuesIfConfigExists() {
      if (this.config && this.config.extra_data) {
        this.setValues();
      } else {
        this.noConfig = true;
      }
    },
    async fetchCountries() {
      const countryChoices = await serviceHandler(
        facilitiesService.getCountries()
      );
      this.countries = countryChoices;
    },

    setValues() {
      this.facilitiesGroups =
        this.config.extra_data.visible_all_facilities === true
          ? "all"
          : this.config.extra_data.visible_facilities_groups;
      this.visibleRatings = [...this.config.extra_data.visible_reviews_ratings];
      this.showOnlyWithText =
        this.config.extra_data.show_only_reviews_with_text || false;
      this.selecteDcountries = this.config.extra_data.search_countries;
    },

    async saveConfig() {
      const updatedConfig = {
        ...this.basicData,
        extra_data: {
          ...this.config.extra_data,
          external_button_link_name: this.floatingButtonData.text,
          external_button_link: this.floatingButtonData.url,
          visible_reviews_ratings: this.visibleRatings,
          visible_all_facilities: this.facilitiesGroups === "all",
          visible_facilities_groups:
            this.facilitiesGroups !== "all" ? this.facilitiesGroups : [],
          show_only_reviews_with_text: this.showOnlyWithText,
          search_countries: this.selecteDcountries
        }
      };
      await serviceHandler(
        storeLocator.saveConfig(this.config.key, updatedConfig)
      );
      if (this.logoFile) {
        await serviceHandler(
          storeLocator.saveFile(this.config.key, this.logoFile, "logo")
        );
      }
      if (this.faviconFile) {
        await serviceHandler(
          storeLocator.saveFile(this.config.key, this.faviconFile, "favicon")
        );
      }
      this.$notify({
        group: "default",
        type: "success",
        text: this.$t("message.storeLocator.saveSuccess")
      });
      this.resetData();
      this.$emit("config-saved");
    },

    resetData() {
      this.basicData = null;
      this.logoFile = null;
      this.faviconFile = null;
      this.facilitiesGroups = null;
      this.visibleRatings = null;
    }
  }
};
</script>

<style lang="scss" scoped>
.sl-config-container {
  display: flex;
  align-items: center;
  min-height: 200px;
}
.sl-card {
  min-height: 589px;
  position: relative;
}

.sl-config {
  padding: $spacing * 4;

  &__row {
    display: flex;
  }

  &__col {
    flex: 1;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
  }
}
</style>
